import { useTranslation } from "react-i18next"

import type { FairUseOverviewFilter, FairUseSummary } from "@/api/admin/fair-use"

import { cn } from "@/lib/utils"

import { formatSekAmount } from "./fair-use-ops-utils"

type StatTileProps = {
  label: string
  value: string
  unit?: string
  sub?: string
  selected: boolean
  attention?: boolean
  onSelect: () => void
}

/**
 * One KPI tile.
 *
 * A real `<button>` with `aria-pressed`, not a decorative div: the tiles ARE
 * the primary filter control (the chip row below is the same state spelled
 * out), so they have to be reachable and announce their on/off state.
 */
function StatTile({ label, value, unit, sub, selected, attention, onSelect }: StatTileProps) {
  return (
    <button
      aria-pressed={selected}
      type="button"
      className={cn(
        "hover:bg-accent/40 focus-visible:ring-ring rounded-xl border p-3 text-left transition-colors focus-visible:ring-1 focus-visible:outline-none",
        selected && "border-primary ring-primary ring-1"
      )}
      onClick={onSelect}
    >
      <div
        className={cn(
          "text-xl leading-tight font-semibold tabular-nums",
          attention && "text-warning-foreground"
        )}
      >
        {value}
        {unit ? (
          <span className="text-muted-foreground ml-1 text-xs font-normal">{unit}</span>
        ) : null}
      </div>
      <div className="text-muted-foreground mt-1 text-xs">{label}</div>
      {sub ? <div className="text-muted-foreground/80 mt-0.5 text-xs">{sub}</div> : null}
    </button>
  )
}

type FairUseKpisProps = {
  summary: FairUseSummary
  filter: FairUseOverviewFilter
  warnPercent?: number
  onFilterChange: (filter: FairUseOverviewFilter) => void
}

export function FairUseKpis({ summary, filter, warnPercent, onFilterChange }: FairUseKpisProps) {
  const { t, i18n } = useTranslation()

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        attention={summary.pending > 0}
        label={t("adminDashboard.fairUse.ops.kpi.pending", "Requests waiting")}
        selected={filter === "pending"}
        value={String(summary.pending)}
        onSelect={() => onFilterChange("pending")}
      />
      <StatTile
        selected={filter === "near"}
        value={String(summary.near_cap)}
        label={
          warnPercent == null
            ? t("adminDashboard.fairUse.ops.kpi.near", "Near their budget")
            : t(
                "adminDashboard.fairUse.ops.kpi.nearWithPercent",
                "Near their budget (≥ {{percent}} %)",
                {
                  percent: warnPercent,
                }
              )
        }
        onSelect={() => onFilterChange("near")}
      />
      <StatTile
        selected={filter === "blocked"}
        value={String(summary.stopped)}
        label={
          summary.enabled
            ? t("adminDashboard.fairUse.ops.kpi.stopped", "Stopped this month")
            : t("adminDashboard.fairUse.ops.kpi.overUnenforced", "Over budget this month")
        }
        onSelect={() => onFilterChange("blocked")}
      />
      <StatTile
        label={t("adminDashboard.fairUse.ops.kpi.spent", "Spent this month")}
        selected={filter === "all"}
        unit="SEK"
        value={formatSekAmount(summary.cost_sek_total, i18n.language)}
        sub={t("adminDashboard.fairUse.ops.kpi.spentUsers", "· {{count}} users", {
          count: summary.users,
        })}
        onSelect={() => onFilterChange("all")}
      />
    </div>
  )
}
